import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Bell, Check, Trash2, X, Inbox } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { onForegroundMessage } from "../lib/fcm";
import { resolveNotificationRoute } from "../lib/notificationRoutes";
import { useAuth } from "../contexts/AuthContext";

/**
 * Notification bell in the top nav.
 *  - Polls /api/notifications every 30s for the badge + recent list
 *  - Listens to FCM foreground messages and bumps the unread count instantly
 *  - Dropdown shows the latest 8 with type-coloured pills
 *  - "See all" goes to /app/notifications
 */
export default function NotificationCenter() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const popRef = useRef(null);
  const panelRef = useRef(null);
  const bellRef = useRef(null);
  const [position, setPosition] = useState(null);

  // Render outside the sticky header so its blur/overflow cannot clip the panel.
  // Keep both the footer and header visible; only the notification list scrolls.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = bellRef.current?.getBoundingClientRect();
      if (!rect) return;
      const viewport = window.visualViewport;
      const width = viewport?.width || window.innerWidth;
      const height = viewport?.height || window.innerHeight;
      const offsetTop = viewport?.offsetTop || 0;
      const offsetLeft = viewport?.offsetLeft || 0;
      const panelWidth = Math.min(384, width - 24);
      const top = Math.max(offsetTop + 12, rect.bottom + 8);
      setPosition({ width: panelWidth, left: Math.max(offsetLeft + 12, Math.min(rect.right - panelWidth, offsetLeft + width - panelWidth - 12)), top, maxHeight: Math.max(0, offsetTop + height - top - (width < 768 ? 90 : 16)) });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    window.visualViewport?.addEventListener("resize", place);
    window.visualViewport?.addEventListener("scroll", place);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      window.visualViewport?.removeEventListener("resize", place);
      window.visualViewport?.removeEventListener("scroll", place);
    };
  }, [open]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications?limit=8");
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch (_) { /* silent — never break the shell */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  // Bump the unread badge instantly when an FCM foreground message arrives
  useEffect(() => {
    let off = () => {};
    try {
      off = onForegroundMessage((payload) => {
        const n = payload?.notification || {};
        const data = payload?.data || {};
        setUnread((u) => u + 1);
        setItems((prev) => [{
          id: data.notification_id || `live-${Date.now()}`,
          title: n.title || "iCaard",
          message: n.body || "",
          type: data.type || "system",
          is_read: false,
          created_at: new Date().toISOString(),
          _live: true,
        }, ...prev].slice(0, 8));
      });
    } catch (_) { /* ignore */ }
    return () => { try { off(); } catch (_) { /* ignore */ } };
  }, []);

  // Close on outside click
  useEffect(() => {
    const h = (e) => { if (open && !popRef.current?.contains(e.target) && !panelRef.current?.contains(e.target)) setOpen(false); };
    const escape = (e) => { if (open && e.key === "Escape") { setOpen(false); bellRef.current?.focus(); } };
    document.addEventListener("mousedown", h);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("mousedown", h); document.removeEventListener("keydown", escape); };
  }, [open]);

  const markOne = async (n) => {
    try {
      if (!n.is_read && !n._live) await api.post(`/notifications/${n.id}/read`);
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      if (!n.is_read) setUnread((u) => Math.max(0, u - 1));
    } catch (_) { /* ignore */ }
  };

  // Click handler: mark-as-read THEN deep-link to the relevant screen.
  const handleClick = (n) => {
    markOne(n);
    const route = resolveNotificationRoute(n, user?.role || "");
    if (route) {
      setOpen(false);
      nav(route);
    }
  };
  const markAll = async () => {
    try { await api.post("/notifications/read-all"); setUnread(0); setItems((p) => p.map((x) => ({ ...x, is_read: true }))); }
    catch (_) { /* ignore */ }
  };
  const remove = async (n, e) => {
    e?.stopPropagation();
    try { await api.delete(`/notifications/${n.id}`); setItems((p) => p.filter((x) => x.id !== n.id)); if (!n.is_read) setUnread((u) => Math.max(0, u - 1)); }
    catch (_) { /* ignore */ }
  };

  return (
    <div className="relative" ref={popRef}>
      <button
        data-testid="notification-bell"
        ref={bellRef}
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        aria-expanded={open}
        aria-controls={open ? "icaard-notifications-popup" : undefined}
        className="relative w-9 h-9 rounded-xl border border-[#26263A] hover:bg-[#13131E] inline-flex items-center justify-center text-white"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span data-testid="notification-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EF4444] text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && position && createPortal(
        <div data-testid="notification-dropdown"
             ref={panelRef} id="icaard-notifications-popup" role="dialog" aria-label="Notifications"
             style={position}
             className="icaard-notifications surface rounded-2xl border border-[#26263A]">
          <div className="icaard-notifications-header px-4 py-3 border-b border-[#26263A] flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#A78BFA]">Notifications</div>
              <div className="font-display font-bold text-base">{unread > 0 ? `${unread} unread` : "All caught up"}</div>
            </div>
            <div className="flex gap-1">
              {unread > 0 && (
                <button data-testid="notification-mark-all" onClick={markAll}
                  className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-[#10B981] hover:bg-[#10B981]/10 rounded inline-flex items-center gap-1">
                  <Check size={11} /> Mark all
                </button>
              )}
              <button onClick={() => { setOpen(false); bellRef.current?.focus(); }} aria-label="Close notifications" className="p-2 text-[#9CA3D4] hover:text-white"><X size={18} /></button>
            </div>
          </div>
          <ul className="icaard-notifications-list" data-testid="notification-list">
            {items.length === 0 && (
              <li className="px-4 py-10 text-center text-sm text-[#9CA3D4] flex flex-col items-center gap-2"><Inbox size={28} /> No notifications yet.</li>
            )}
            {items.map((n) => (
              <li key={n.id} data-testid={`notification-item-${n.id}`}
                  className={`px-4 py-3 border-b border-[#26263A] last:border-0 hover:bg-[#13131E] ${!n.is_read ? "bg-[#7C3AED]/8" : ""}`}>
                <div className="flex items-start gap-2">
                  <button type="button" onClick={() => handleClick(n)} className="icaard-notification-content flex-1 min-w-0 text-left">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-[9px] tracking-[0.15em] uppercase font-bold ${typeColor(n.type)}`}>{n.type || "system"}</span>
                      {!n.is_read && <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444]" />}
                    </div>
                    <div className="font-semibold text-sm text-white">{n.title}</div>
                    <div className="text-xs text-[#9CA3D4] mt-1">{n.message}</div>
                    <div className="text-[10px] text-[#7C3AED] mt-1">{new Date(n.created_at).toLocaleString()}</div>
                  </button>
                  <button onClick={(e) => remove(n, e)} className="text-[#9CA3D4] hover:text-[#EF4444] p-1 rounded" aria-label="Delete">
                    <Trash2 size={12} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <button data-testid="notification-see-all" onClick={() => { setOpen(false); nav("/app/notifications"); }}
            className="w-full px-4 py-2.5 text-center text-xs font-bold uppercase tracking-wider text-[#7C3AED] hover:bg-[#13131E] border-t border-[#26263A]">
            See all & manage
          </button>
        </div>, document.body
      )}
    </div>
  );
}

const typeColor = (t) => {
  const map = {
    announcement: "text-[#FACC15]",
    card_activated: "text-[#10B981]",
    card_replaced: "text-[#10B981]",
    card_scanned: "text-[#A78BFA]",
    profile_viewed: "text-[#06B6D4]",
    transport: "text-[#10B981]",
    ticket: "text-[#7C3AED]",
    wallet: "text-[#10B981]",
    connection: "text-[#A78BFA]",
    message: "text-[#3B82F6]",
    subscription: "text-[#F59E0B]",
    system: "text-[#9CA3D4]",
  };
  return map[t] || "text-[#9CA3D4]";
};
